// Canonical instructor data for workshop detail pages.
// Bios are condensed from the Faculty page (src/components/Faculty/Faculty.js)
// and the Summer 2026 Workshop Launch Copy. Headshots reuse the existing
// Faculty assets. Workshops reference instructors by name (workshop.instructors),
// and WorkshopDetail looks them up here — so bios live in one place, not in Firestore.

import graceHeadshot from '../assets/GraceHeadshot.jpeg';
import anthonyHeadshot from '../assets/ApicellaHeadshot2.png';
import anneliseHeadshot from '../assets/Annelise Wall Headshot (r-cu).jpg';

const INSTRUCTORS = {
	'Grace Wall': {
		name: 'Grace Wall',
		role: 'Co-Owner / Teaching Artist',
		photo: graceHeadshot,
		bio:
			"An LA Weekly Award-winning actress, Grace trained in musical theatre at UCLA and " +
			"in Bel Canto with Tony Award-winner Victoria Clark. She has performed from the " +
			"Hollywood Bowl to Lincoln Center, with television credits including 90210 and " +
			"NBC's The Voice.",
	},
	'Anthony Apicella': {
		name: 'Anthony Apicella',
		role: 'Co-Owner / Teaching Artist',
		photo: anthonyHeadshot,
		bio:
			'A working professional actor trained at the American Musical and Dramatic ' +
			'Academy in NYC, Anthony has played leading roles on the New York stage and ' +
			"tours the world as a symphony soloist, including 'Disney in Concert.'",
	},
	'Annelise Wall': {
		name: 'Annelise Wall',
		role: 'Teaching Artist',
		photo: anneliseHeadshot,
		bio:
			'Annelise holds a BFA in Theatre with a music minor from Southern Methodist ' +
			'University. A versatile singer with over 13 years of training, she specializes in ' +
			'musical theatre and R&B, and has taught voice and theatre for more than a decade.',
	},
};

// Per-workshop bio overrides, keyed by workshop id (slug) → instructor name.
// When an entry exists it replaces that instructor's shared `bio` on that
// workshop only; otherwise the shared default above is used. This lets a teacher
// read differently per workshop (e.g. an All-State / acting / audition emphasis)
// without duplicating the whole record. Bios stay in the component layer.
const WORKSHOP_BIO_OVERRIDES = {
	'songwriting-workshop': {
		'Annelise Wall':
			"Annelise began writing songs for her children's choir around age 6, and she has " +
			"been writing music ever since. She holds a BFA in Theatre with a music minor from " +
			"Southern Methodist University. A versatile singer with over 13 years of training, " +
			"she specializes in musical theatre and R&B, and has taught voice and theatre for " +
			"more than a decade. Annelise has written many songs and composed soundtracks for " +
			"multiple plays.",
	},
	'audition-foundations': {
		'Grace Wall':
			'studied Bel Canto with Tony Award-winner Victoria Clark and trained in ' +
			'musical theatre at UCLA, performing from the Hollywood Bowl to Lincoln Center. ' +
			'An All-State singer herself, she knows firsthand the musicianship and discipline ' +
			'an All-State experience rewards.',
		'Anthony Apicella':
			'Trained at AMDA and a soloist with symphonies around the world, Anthony brings a ' +
			'classical and concert-stage background to the panel. An All-State singer himself, ' +
			"he's sat exactly where your singer is sitting.",
		'Annelise Wall':
			"Annelise holds a BFA in Theatre with a music minor from Southern Methodist " +
			"University. A versatile singer with over 13 years of training, she specializes in " +
			"musical theatre and R&B, and has taught voice and theatre for more than a decade. " +
			"She was an All-State choir singer herself.",
	},
	'acting-boot-camp': {
		'Anthony Apicella':
			'A working professional actor trained at AMDA in NYC, Anthony has played leading ' +
			'roles on the New York stage, including the rock musical Rock of Ages. He brings ' +
			'real scene work, presence, and stagecraft into the room.',
	},
	'mt-audition-workshop': {
		'Anthony Apicella':
			'Trained at AMDA in NYC, Anthony is a working professional actor and symphony ' +
			'soloist who has built a career across musical theatre and the audition room. He ' +
			'coaches the song choices, cuts, and presence that turn nerves into preparation.',
	},
};

// Look up instructor records for a workshop's instructor-name list. Pass the
// workshop id (slug) to apply any per-workshop bio override; omit it for the
// shared defaults. Unknown names are skipped so the page never breaks on a typo.
export function getInstructors(names, workshopId) {
	if (!Array.isArray(names)) return [];
	const overrides = (workshopId && WORKSHOP_BIO_OVERRIDES[workshopId]) || {};
	return names
		.map((n) => {
			const base = INSTRUCTORS[n];
			if (!base) return null;
			return overrides[n] ? { ...base, bio: overrides[n] } : base;
		})
		.filter(Boolean);
}

export default INSTRUCTORS;
